
import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    document.title = 'Revenue Growth Insights & AI Automation Blog | SparkBoost Labs Sacramento';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert insights on AI revenue systems, sales automation, and predictable growth strategies for Sacramento service businesses. Learn from industry leaders and case studies.');
    }
  }, []);

  const blogPosts = [
    {
      id: 1,
      title: 'The Complete Guide to AI-Powered Outbound Automation',
      excerpt: 'Learn how to implement AI-driven outbound systems that generate consistent leads while you focus on closing deals.',
      author: 'Marcus Chen',
      date: 'December 15, 2024',
      readTime: '8 min read',
      category: 'Automation',
      image: 'https://public.readdy.ai/ai/img_res/7ff1b18727df0e889d8be338a499f8aa.jpg',
      slug: 'ai-outbound-automation-guide'
    },
    {
      id: 2,
      title: 'Sales Process Optimization: From Chaos to Predictable Revenue',
      excerpt: 'Transform your sales process from unpredictable to systematic with proven frameworks that scale.',
      author: 'Sarah Rodriguez',
      date: 'December 12, 2024',
      readTime: '6 min read',
      category: 'Sales',
      image: 'https://readdy.ai/api/search-image?query=clear%20sales%20pipeline%20visualization%20on%20whiteboard%20with%20distinct%20stages%20and%20arrows%2C%20professional%20business%20meeting%20room%20with%20visible%20charts%20and%20metrics%2C%20bright%20lighting%20with%20teal%20and%20navy%20blue%20markers%2C%20high%20contrast%20business%20photography&width=400&height=250&seq=blog2clear&orientation=landscape',
      slug: 'sales-process-optimization'
    },
    {
      id: 3,
      title: 'Building Authority Through Strategic Content Marketing',
      excerpt: 'Discover how thought leadership content can position you as the go-to expert in your industry.',
      author: 'David Park',
      date: 'December 10, 2024',
      readTime: '7 min read',
      category: 'Content',
      image: 'https://readdy.ai/api/search-image?query=professional%20content%20creator%20workspace%20with%20laptop%20showing%20clear%20blog%20interface%2C%20organized%20desk%20with%20notebooks%20and%20coffee%2C%20bright%20natural%20lighting%2C%20modern%20office%20environment%20with%20teal%20and%20navy%20blue%20accents%2C%20high%20contrast%20professional%20photography&width=400&height=250&seq=blog3clear&orientation=landscape',
      slug: 'authority-building-content'
    },
    {
      id: 4,
      title: 'Revenue Operations: The Framework for Scalable Growth',
      excerpt: 'Build a revenue operations system that aligns sales, marketing, and customer success for predictable growth.',
      author: 'Lisa Thompson',
      date: 'December 8, 2024',
      readTime: '9 min read',
      category: 'Strategy',
      image: 'https://readdy.ai/api/search-image?query=clear%20business%20strategy%20meeting%20with%20visible%20revenue%20charts%20on%20wall%2C%20professional%20team%20discussing%20growth%20metrics%2C%20bright%20conference%20room%20with%20clear%20whiteboards%20showing%20business%20processes%2C%20teal%20and%20navy%20blue%20corporate%20colors%2C%20high%20contrast%20business%20photography&width=400&height=250&seq=blog4clear&orientation=landscape',
      slug: 'revenue-operations-framework'
    },
    {
      id: 5,
      title: 'Transitioning from Founder-Led Sales to Scalable Systems',
      excerpt: 'Learn how to systematize your sales process so your business can grow beyond your personal involvement.',
      author: 'Michael Foster',
      date: 'December 5, 2024',
      readTime: '10 min read',
      category: 'Growth',
      image: 'https://readdy.ai/api/search-image?query=professional%20business%20scaling%20concept%20with%20clear%20organizational%20chart%20on%20wall%2C%20modern%20office%20with%20visible%20growth%20metrics%20and%20team%20structure%20diagrams%2C%20bright%20lighting%20with%20teal%20and%20navy%20blue%20corporate%20elements%2C%20high%20contrast%20business%20photography&width=400&height=250&seq=blog5clear&orientation=landscape',
      slug: 'founder-led-sales-transition'
    },
    {
      id: 6,
      title: 'Essential AI Tools Every Service Business Should Use',
      excerpt: 'Discover the AI tools that can automate your operations and accelerate your business growth.',
      author: 'Jennifer Walsh',
      date: 'December 3, 2024',
      readTime: '5 min read',
      category: 'Technology',
      image: 'https://readdy.ai/api/search-image?query=clear%20technology%20workspace%20with%20multiple%20computer%20screens%20showing%20AI%20software%20interfaces%2C%20modern%20office%20setup%20with%20visible%20tech%20tools%20and%20applications%2C%20bright%20professional%20lighting%20with%20teal%20and%20navy%20blue%20tech%20elements%2C%20high%20contrast%20business%20photography&width=400&height=250&seq=blog6clear&orientation=landscape',
      slug: 'ai-tools-service-businesses'
    }
  ];

  const categories = ['All', 'Automation', 'Sales', 'Content', 'Strategy', 'Growth', 'Technology'];

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#1B365D] to-[#2BB6A3] py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl font-bold mb-6">
                Revenue Growth Insights & Strategies
              </h1>
              <p className="text-xl mb-8 opacity-90">
                Expert insights on AI automation, sales optimization, and predictable growth strategies 
                for service-based businesses. Learn from real case studies and proven methodologies.
              </p>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-8 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                    selectedCategory === category
                      ? 'bg-[#2BB6A3] text-white border-[#2BB6A3]'
                      : 'border-[#2BB6A3] text-[#2BB6A3] hover:bg-[#2BB6A3] hover:text-white'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {filteredPosts.map((post) => (
                <article key={post.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="relative">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-48 object-cover object-top"
                    />
                    <div className="absolute top-4 left-4 bg-[#2BB6A3] text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {post.category}
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <span>{post.date}</span>
                      <span className="mx-2">•</span>
                      <span>{post.readTime}</span>
                    </div>
                    
                    <h2 className="text-xl font-bold text-[#1B365D] mb-3 line-clamp-2">
                      {post.title}
                    </h2>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    
                    <a href={`/blog/${post.slug}`} className="text-[#2BB6A3] font-semibold hover:text-[#239B8A] transition-colors cursor-pointer">
                      Read More →
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter Signup */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-[#1B365D] mb-4">
                Stay Updated with Revenue Growth Insights
              </h2>
              <p className="text-gray-600 mb-8">
                Get weekly insights on AI automation, sales optimization, and proven growth strategies 
                delivered directly to your inbox.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2BB6A3] focus:border-transparent text-sm"
                />
                <button className="bg-[#2BB6A3] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#239B8A] transition-colors whitespace-nowrap cursor-pointer">
                  Subscribe
                </button>
              </div>
              
              <p className="text-xs text-gray-500 mt-4">
                No spam. Unsubscribe anytime. Read our privacy policy.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
